import React from 'react'
import { useQuery } from 'react-query'
import { useSearchParams, Link } from 'react-router-dom'

import Button from 'components/Button'
import { postReportVoucherRedemptionCountService } from 'api/reports-voucher-redemption-count'
import { ExportToCSV } from '../../../reports-list/utils-reports-actions'

type VoucherIssue = { voucherId: number; issueCount: number }
type VoucherRedemptionItem = { unit: string; voucherId: number; redemptionCount: number }

const PieChart = ({ data }: { data: { label: string; value: number }[] }) => {
  const total = data.reduce((s, d) => s + d.value, 0)
  let cumulative = 0
  const radius = 80
  const cx = 100
  const cy = 100

  const sectorPath = (start: number, end: number) => {
    const startRad = (start / total) * Math.PI * 2 - Math.PI / 2
    const endRad = (end / total) * Math.PI * 2 - Math.PI / 2
    const x1 = cx + radius * Math.cos(startRad)
    const y1 = cy + radius * Math.sin(startRad)
    const x2 = cx + radius * Math.cos(endRad)
    const y2 = cy + radius * Math.sin(endRad)
    const large = end - start > total / 2 ? 1 : 0
    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`
  }

  return (
    <svg width={200} height={200} viewBox="0 0 200 200">
      {data.map((d, i) => {
        const start = cumulative
        cumulative += d.value
        const end = cumulative
        const path = sectorPath(start, end)
        const color = `hsl(${(i * 60) % 360} 70% 50%)`
        return <path key={d.label} d={path} fill={color} stroke="#fff" />
      })}
    </svg>
  )
}

export const ViewVoucherRedemption = () => {
  const [searchParams] = useSearchParams()
  const searchQuery = JSON.parse(decodeURIComponent(searchParams.get('report-query')!))

  const { data, isLoading, isError } = useQuery(['voucherRedemptionReport', searchQuery], () => postReportVoucherRedemptionCountService(searchQuery))

  if (isLoading) return <div>Loading...</div>
  if (isError) return <div>Error loading report</div>

  const apiData = data?.data

  const redemptionByVoucher = (apiData?.voucherRedemptionCounts || []).reduce<Record<number, number>>((acc, item: VoucherRedemptionItem) => {
    acc[item.voucherId] = (acc[item.voucherId] || 0) + item.redemptionCount
    return acc
  }, {})

  const pieData = Object.entries(redemptionByVoucher).map(([voucherId, value]) => ({ label: voucherId, value }))

  return (
    <div className="my-6">
      <div className="flex items-center gap-4">
        <button className="px-3 py-1 border rounded-md border-primary text-primary" type="button">
          <Link to={`/reports-list?modal=report_2`} className="flex items-center gap-2">
            <span className="tracking-wider font-light">Back to reports</span>
          </Link>
        </button>

        <Button type="button" className="ml-1 rounded-lg tracking-wide font-bold" iconRight={false} onClick={() => ExportToCSV(apiData?.voucherRedemptionCounts || [], { filename: 'Voucher-Redemption' })}>
          Download CSV Report
        </Button>
      </div>

      <br />

      <div className="flex gap-8 items-start">
        <div>
          <h4 className="font-bold">Redemptions by Voucher</h4>
          {pieData.length ? <PieChart data={pieData} /> : <p>No redemption data available</p>}
        </div>

        <div>
          <h4 className="font-bold">Issue Counts</h4>
          <table className="table-auto border-collapse">
            <thead>
              <tr>
                <th className="px-2">Voucher ID</th>
                <th className="px-2">Issued</th>
              </tr>
            </thead>
            <tbody>
              {(apiData?.voucherIssueCounts || []).map((row: VoucherIssue) => (
                <tr key={row.voucherId}>
                  <td className="px-2">{row.voucherId}</td>
                  <td className="px-2">{row.issueCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ViewVoucherRedemption
